import React, { FC, useRef, useEffect } from 'react'
import { ISectionInteractiveRowsProps } from './types'

export const SectionInteractiveRows: FC<ISectionInteractiveRowsProps> = ({
  items,
  variant = 'default',
  rowsRef,
}) => {
  const internalRef = useRef<HTMLUListElement>(null)
  const effectiveRef = rowsRef || internalRef

  const isRadar = variant === 'radar'
  const isCompact = variant === 'compact'

  useEffect(() => {
    if (typeof window === 'undefined' || !isRadar || !effectiveRef.current) return

    const updateMaxHeight = () => {
      const rows = Array.from(effectiveRef.current?.children || []) as HTMLElement[]
      if (!rows.length) return

      rows.forEach((row) => {
        row.style.minHeight = ''
      })

      if (window.innerWidth < 768) return

      let maxHeight = 0
      rows.forEach((row) => {
        const h = row.offsetHeight
        if (h > maxHeight) maxHeight = h
      })

      if (maxHeight > 0) {
        rows.forEach((row) => {
          row.style.minHeight = `${maxHeight}px`
        })
      }
    }

    updateMaxHeight()
    const timeout = setTimeout(updateMaxHeight, 150)
    if (document.fonts?.ready) {
      document.fonts.ready.then(updateMaxHeight)
    }

    window.addEventListener('resize', updateMaxHeight, { passive: true })
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', updateMaxHeight)
    }
  }, [isRadar, items, effectiveRef])

  if (!items || items.length === 0) return null

  const rowPaddingClass = isRadar
    ? 'py-5 sm:py-6 md:py-7 md:min-h-[104px] grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-8 items-center'
    : isCompact
      ? 'py-5 sm:py-6 md:py-6 lg:py-7 md:min-h-[108px] flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-8'
      : 'py-8 md:py-12 lg:py-14 md:min-h-[148px] flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-8'

  const leftColClass = isRadar ? 'col-span-1 md:col-span-4' : 'flex-1'

  const arrowWidthClass = isRadar
    ? 'md:group-hover:w-6 sm:md:group-hover:w-7 md:group-hover:w-8 md:group-focus-visible:w-6'
    : isCompact
      ? 'md:group-hover:w-6 sm:md:group-hover:w-8 md:group-hover:w-10 md:group-focus-visible:w-6 sm:md:group-focus-visible:w-8 md:group-focus-visible:w-10'
      : 'md:group-hover:w-8 sm:md:group-hover:w-10 md:group-hover:w-12 md:group-focus-visible:w-8'

  const arrowSvgSize = isRadar
    ? 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7'
    : isCompact
      ? 'w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8'
      : 'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10'

  const titleFontSize = isRadar
    ? 'text-title-medium sm:text-title-large md:text-title-large lg:text-title-large xl:text-headline-medium md:pb-1.5'
    : isCompact
      ? 'text-xl sm:text-2xl md:text-2xl lg:text-2xl xl:text-4xl md:pb-1'
      : 'text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-6xl md:pb-2'

  const indexBadgeSize = isRadar
    ? 'text-label-large sm:text-body-small'
    : isCompact
      ? 'text-xs sm:text-sm'
      : 'text-xs sm:text-sm md:text-base'

  const rightColWidth = isRadar
    ? 'col-span-1 md:col-span-8 w-full'
    : 'w-full md:w-80 lg:w-[480px] xl:w-[540px] shrink-0'

  return (
    <div className="w-full">
      <ul ref={effectiveRef} className="w-full border-t border-dashed border-outline-lower flex flex-col">
        {items.map((item, index) => {
          const itemTitle = typeof item === 'string' ? item : item.title
          const itemText =
            typeof item === 'string'
              ? ''
              : item.text || item.description || item.content || ''

          return (
            <li
              key={index}
              tabIndex={0}
              className={`opacity-0 group w-full border-b border-dashed border-outline-lower ${rowPaddingClass} px-0 md:px-6 cursor-default focus:outline-none`}
            >
              <div className={`${leftColClass} flex items-center md:gap-6 min-w-0 w-full`}>
                <span
                  className={`hidden md:flex text-primary w-0 overflow-hidden opacity-0 -translate-x-4 ${arrowWidthClass} md:group-hover:opacity-100 md:group-hover:translate-x-0 md:group-focus-visible:opacity-100 md:group-focus-visible:translate-x-0 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 items-center`}
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${arrowSvgSize} shrink-0`}
                  >
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <polyline points="15 6 21 12 15 18" />
                  </svg>
                </span>
                <div className="flex items-start justify-between md:justify-start md:gap-3 w-full md:transition-transform md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)] md:group-hover:translate-x-2 md:group-focus-visible:translate-x-2 min-w-0 py-0 md:py-1">
                  <span
                    className={`font-palanquin-dark font-normal ${titleFontSize} leading-tight text-on-surface md:text-on-surface-subtle md:group-hover:text-on-surface md:group-focus-visible:text-on-surface md:transition-colors md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)] min-w-0`}
                  >
                    {itemTitle}
                  </span>
                  <span
                    className={`font-mono ${indexBadgeSize} font-bold text-on-primary-subtle shrink-0 ml-auto md:ml-0 text-right`}
                  >
                    [{String(index + 1).padStart(2, '0')}]
                  </span>
                </div>
              </div>

              {itemText && (
                <div
                  className={`${rightColWidth} text-left opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100 md:translate-x-3 md:group-hover:translate-x-0 md:transition-all md:duration-700 md:ease-[cubic-bezier(0.65,0,0.35,1)]`}
                >
                  <div className="hidden md:flex items-center gap-extra-small mb-1.5 md:mb-2 lg:mb-1 justify-start">
                    <span
                      className="w-base h-base rounded-container-extra-small bg-primary inline-flex items-center justify-center shrink-0 text-on-primary-unstable"
                      aria-hidden="true"
                    >
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-extra-small h-extra-small"
                        aria-hidden="true"
                      >
                        <line x1="6" y1="2" x2="6" y2="10" />
                        <line x1="2" y1="6" x2="10" y2="6" />
                      </svg>
                    </span>
                    <span className="font-palanquin-dark font-bold text-label-large sm:text-body-small uppercase tracking-wider text-on-surface-subtle group-hover:text-on-surface transition-colors duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]">
                      {itemTitle}
                    </span>
                  </div>
                  <p
                    className={`font-palanquin ${
                      isRadar ? 'text-body-large' : 'text-body-large md:text-body-large'
                    } text-on-surface font-normal leading-relaxed text-left ${
                      isRadar ? 'w-full' : 'text-balance [text-wrap:balance] max-w-10/12 md:max-w-none min-h-[2lh] md:min-h-0'
                    } ${
                      isRadar ? 'line-clamp-4' : 'line-clamp-3'
                    }`}
                  >
                    {itemText}
                  </p>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default SectionInteractiveRows
